import type {
  CustomCart,
  CustomCartItem,
  UpsellItem,
} from '@bulknutrients/bulk-shared-lib/interfaces/ICart';
import type { BaseProduct } from '@bulknutrients/bulk-shared-lib/interfaces/Product';
import { cartValueWithoutGiftCertificates } from '@bulknutrients/bulk-shared-lib/lib/cart';
import {
  CLOUDINARY_ASSET_FIELDS,
  IMAGE_FIELDS,
} from '@bulknutrients/bulk-shared-lib/lib/queries/sanity/snippets';
import { client } from '@bulknutrients/bulk-shared-lib/lib/sanity/client';
import { BulkErrorBoundary } from '@components/Alerts/ErrorBoundaryFallback';
import { AddToCartStatus } from '@components/Button/AddToCart';
import { BigCommerceCheckoutButton } from '@components/Button/BigCommerceCheckoutButton';
import {
  UIContext,
  type UIContextInterface,
} from '@components/Context/UIContext/UIContext';
import { Icons } from '@components/Icons/Icons';
import { ProductCardQuickCartCarousel } from '@components/ProductCard/ProductCard';
import { SlideInPanel } from '@components/SlideInPanel/SlideInPanelNoPortal';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { useCart } from '@hooks/cart/useCart';
import { useCustomerStatus } from '@hooks/customer/useCustomerStatus';
import { useRouter } from 'next/router';
import {
  type ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useSWR from 'swr';
import { CartPrice } from '../CartPrice';
import { CartWeightIndicator } from '../CartWeightIndicator';
import { EmptyCartMessage } from '../EmptyCartMessage';
import { CreateAccountLoyaltyPrompt } from '../LoyaltyPrompt';
import { QuickCartGiftCertificate } from './QuickCartGiftCertificate';
import { QuickCartItem } from './QuickCartItem';
import { QuickCartUpsell } from './QuickCartUpsell';

function CartTotal({
  cart,
  items,
}: {
  cart?: CustomCart | null;
  items: CustomCartItem[];
}): ReactElement {
  const [subtotal, setSubtotal] = useState(0);
  const [cartAmount, setCartAmount] = useState(0);

  useEffect(() => {
    let mounted = true;
    if (cart && mounted) {
      const sub = [...cart.custom_items].reduce(
        (acc: number, item: CustomCartItem) => {
          acc +=
            item.quantity *
            items.filter(
              (cur: CustomCartItem) =>
                cur.variant?.variantId === item.variant_id
            )[0]?.original_price;
          return acc;
        },
        0
      );

      const withGiftCerts =
        sub +
        cart.line_items.gift_certificates.reduce(
          (acc, cur) => acc + cur.amount,
          0
        );

      setSubtotal(withGiftCerts || cart.cart_amount);
      setCartAmount(cart.cart_amount);
    }
    return function cleanup() {
      mounted = false;
    };
  }, [cart, items]);

  if (cart !== null && cart !== undefined && items.length > 0) {
    return <CartPrice retail={subtotal} discounted={cartAmount} />;
  }
  return <></>;
}

const fetcher = async (url) => {
  const query = `//groq
    *[_type == "quickCartUpsell" 
    && !(_id in path("drafts.**")) 
    && dateTime(startsAt) < dateTime(now())
    && dateTime(expiresAt) > dateTime(now())
    ][0]
    {
      message,
      "image": select(
        image.asset._type == "cloudinary.asset" => image{
          ${CLOUDINARY_ASSET_FIELDS}
        },
        image._type == "image" => image{
          ${IMAGE_FIELDS}
      }),
      "variant": variant->{
        variantId, 
        sku,
        productId,
      },
    }
  `;

  const result = await client.fetch(query);
  if (!result) {
    const error = new Error('Unable to fetch quick cart upsell item');
    throw error;
  }
  return result;
};

export function TailoredCartUpsell(): ReactElement {
  const [upsellItem, setUpsellItem] = useState<BaseProduct | null>(null);

  const { suspectedCustomerId } = useCustomerStatus();
  const { cart } = useCart();

  useEffect(() => {
    let mounted = true;
    const fetchUpsell = async () => {
      const response = await fetch('/api/customer/upsell', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          suspectedCustomerId: suspectedCustomerId,
        }),
      });

      if (response.status === 200) {
        const { data }: { data: BaseProduct | null } = await response.json();
        if (mounted) {
          setUpsellItem(data);
        }
      }
    };

    // only fetch if cart exists with items
    if (cart?.custom_items.length) fetchUpsell();

    return function cleanup() {
      mounted = false;
    };
  }, [suspectedCustomerId, cart]); // cart is included to re-fetch upsell when cart changes (e.g., item added/removed)

  return upsellItem ? (
    <div className="mx-2 sm:mx-auto mb-2">
      <div className="flex items-center my-1">
        <span className="border-b border-grey-mid m-2 w-1" />
        <span className="uppercase font-bold text-sm">You might also like</span>
        <span className="border-b border-grey-mid grow m-2" />
      </div>
      <ProductCardQuickCartCarousel
        product={upsellItem}
        location="tailored upsell"
      />
    </div>
  ) : (
    <div className="my-1" />
  );
}

export function QuickCart(): ReactElement {
  const { cart, error } = useCart();
  const { quickCartIsOpen, setQuickCartIsOpen, addingToCartStatus } =
    useContext<UIContextInterface>(UIContext);

  const [showUpsellBanner, setShowUpsellBanner] = useState(true);

  // Extract line items from cart (memo no need for async)
  const cartItems = useMemo(() => {
    if (cart?.custom_items) {
      return [...cart.custom_items];
    }
    return [];
  }, [cart?.custom_items]);

  // Extract gift certificates from cart (memo no need for async)
  const giftCertificates = useMemo(() => {
    if (cart?.line_items.gift_certificates) {
      return [...cart.line_items.gift_certificates];
    }
    return [];
  }, [cart]);

  // periodically get upsell banner
  const { data: upsellItem } = useSWR<UpsellItem>(
    '/api/quickCartUpsell',
    fetcher,
    {
      refreshInterval: 1200000, // 20 minutes
      revalidateOnFocus: false,
    }
  );

  const router = useRouter();

  // Work out if upsell banner should be displayed
  useEffect(() => {
    let mounted = true;
    setShowUpsellBanner(true);
    // 1. hide upsell banner when cart is empty
    if (cartItems.length === 0) {
      if (mounted) setShowUpsellBanner(false);
    }

    // 2. hide upsell banner when cart has upsell item already
    if (
      cartItems.findIndex((item) => item.sku === upsellItem?.variant.sku) > -1
    ) {
      if (mounted) setShowUpsellBanner(false);
    }

    return function cleanup() {
      mounted = false;
    };
  }, [addingToCartStatus, cartItems, upsellItem]);

  // close the quick cart on route change
  useEffect(() => {
    const handleRouteChange = () => {
      setQuickCartIsOpen(false);
    };

    router.events.on('routeChangeStart', handleRouteChange);

    // If the component is unmounted, unsubscribe
    // from the event with the `off` method:
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, []);

  if (error) {
    return (
      <SlideInPanel
        isOpen={quickCartIsOpen}
        displayCloseX={false}
        toggleState={setQuickCartIsOpen}
        testId="quick-cart"
      >
        <div className="flex justify-between items-center gap-2 mx-4 my-1">
          <h2 className="mb-1 mt-2.5">Your Cart</h2>
          <button
            type="button"
            onClick={() => setQuickCartIsOpen(false)}
            className="opacity-50 border-none bg-black dark:bg-white rounded-full font-secondary text-4xl text-white dark:text-black hover:cursor-pointer hover:text-orange hover:no-underline focus:cursor-pointer focus:text-orange focus:no-underline"
          >
            <Icons.Close />
          </button>
        </div>
        <Paragraph>
          We were unable to retrieve a cart from the server at this time.
        </Paragraph>
        <pre>{error.message}</pre>
      </SlideInPanel>
    );
  }

  if (
    ((cart && cartItems.length === 0 && giftCertificates.length === 0) ||
      cart === undefined ||
      cart == null) &&
    addingToCartStatus !== AddToCartStatus.Adding
  ) {
    return (
      <SlideInPanel
        isOpen={quickCartIsOpen}
        displayCloseX={false}
        toggleState={setQuickCartIsOpen}
        testId="quick-cart"
      >
        <div className="flex justify-between items-center gap-2 mx-4 my-1">
          <h2 className="mb-1 mt-2.5">Your Cart</h2>
          <button
            type="button"
            onClick={() => setQuickCartIsOpen(false)}
            className="opacity-50 border-none bg-black dark:bg-white rounded-full font-secondary text-4xl text-white dark:text-black hover:cursor-pointer hover:text-orange hover:no-underline focus:cursor-pointer focus:text-orange focus:no-underline"
          >
            <Icons.Close />
          </button>
        </div>
        <div>
          <EmptyCartMessage />
        </div>
      </SlideInPanel>
    );
  }

  return (
    <SlideInPanel
      isOpen={quickCartIsOpen}
      toggleState={setQuickCartIsOpen}
      scrollable={false}
      displayCloseX={false}
      testId="quick-cart"
    >
      <section className="flex h-full flex-col justify-between">
        <BulkErrorBoundary location="QuickCart">
          {/* Heading & Upsell */}
          <div className="shadow-[0_3px_5px_rgba(0,0,0,0.1)] dark:shadow-none dark:border-b-2 dark:border-grey-40">
            <div className="flex justify-between items-center gap-2 mx-4 mt-1">
              <h2 className="mb-1 mt-2.5">Your Cart</h2>
              <button
                type="button"
                onClick={() => setQuickCartIsOpen(false)}
                className="opacity-50 border-none bg-black dark:bg-white rounded-full font-secondary text-4xl text-white dark:text-black hover:cursor-pointer hover:text-orange hover:no-underline focus:cursor-pointer focus:text-orange focus:no-underline"
              >
                <Icons.Close />
              </button>
            </div>
            <QuickCartUpsell
              showUpsellBanner={showUpsellBanner}
              upsellItem={upsellItem}
              key="upsellItem"
            />
          </div>

          {/* Items */}
          <div className="grow overflow-y-scroll relative ">
            {cartItems.map((lineItem: CustomCartItem) => {
              return <QuickCartItem item={lineItem} key={lineItem.id} />;
            })}
            {giftCertificates.map((cert) => (
              <QuickCartGiftCertificate key={cert.id} certificate={cert} />
            ))}
          </div>

          {cart && <CartWeightIndicator weight={cart.weight} />}

          {/* Targeted Upsell */}
          <TailoredCartUpsell />

          {/* Total and Checkout */}
          <div className="flex gap-4 mx-4 items-center bg-white dark:bg-black">
            <div className="flex flex-col">
              <span className="text-grey-50 text-xs">Subtotal:</span>
              <CartTotal cart={cart} items={cartItems} />
            </div>

            <BigCommerceCheckoutButton
              styledButton={true}
              location="Quick cart"
              className="grow"
            />
          </div>

          {cart ? (
            <CreateAccountLoyaltyPrompt
              cartValue={cartValueWithoutGiftCertificates(cart)}
            />
          ) : null}
        </BulkErrorBoundary>
      </section>
    </SlideInPanel>
  );
}
